import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { IRequestState } from './interfaces';
import {
  GetDocTypesRequest,
  ICustomerDto,
  IDocTypeDto,
  IGetCopyDestinationDocTypesForTargetDBRequest,
  IGetCopyDestinationDocTypesForTargetDBResponse,
  GetCopyDestinationDocTypesForTargetDBRequest,
} from '../api/client';
import Api from '../api/helpers';

export interface IDocTypesState extends IRequestState {
  docTypesByActiveCustomer: IDocTypeDto[];
  activeDocType: IDocTypeDto | null;
}

const initialState: IDocTypesState = {
  docTypesByActiveCustomer: [],
  activeDocType: null,
  isRequestActive: false,
  errorDetails: { statusCode: null, userMessage: null },
};

const docTypesSlice = createSlice({
  name: 'docTypes',
  initialState,
  reducers: {
    setActiveDocType: (state, action: PayloadAction<IDocTypeDto>) => {
      state.activeDocType = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(fetchDocTypesByCustomer.pending, (state) => {
      state.isRequestActive = true;
    });
    builder.addCase(fetchDocTypesByCustomer.fulfilled, (state, action) => {
      state.isRequestActive = false;
      state.docTypesByActiveCustomer = action.payload.docTypes;
      state.activeDocType = action.payload.activeDocType;
    });
  },
});

export const fetchDocTypesByCustomer = createAsyncThunk<
  { docTypes: IDocTypeDto[]; activeDocType: IDocTypeDto },
  { customer: ICustomerDto; docTypeIdInUrl: string | undefined }
>('docTypes/fetchDocTypes', async (request, thunkApi) => {
  const client = Api.getDocTypesClient(thunkApi.dispatch);
  const response = await client.getDocTypes(
    new GetDocTypesRequest({ system: request.customer.sysPrin.system, principle: request.customer.sysPrin.principle }),
  );

  let defaultDocType = response.docTypes[0];
  if (request.docTypeIdInUrl) {
    const urlDocType = response.docTypes.find((dt) => dt.id === request.docTypeIdInUrl);
    if (urlDocType) defaultDocType = urlDocType;
  }

  return { docTypes: response.docTypes, activeDocType: defaultDocType };
});

export const fetchCopyDestinationDocTypes = createAsyncThunk<
  IGetCopyDestinationDocTypesForTargetDBResponse,
  IGetCopyDestinationDocTypesForTargetDBRequest
>('docTypes/getCopyDestinationDocTypes', async (request, thunkApi) => {
  const client = Api.getDocTypesClient(thunkApi.dispatch);
  const response = await client.getDocTypesForTargetDB(new GetCopyDestinationDocTypesForTargetDBRequest(request));
  return response;
});

export const { setActiveDocType } = docTypesSlice.actions;
export default docTypesSlice.reducer;
